import EventEmitter from 'events';
import { current, produce, original } from 'immer';
import { clamp, max, trim } from 'lodash';
import { Nullable } from 'src/types/nullable.type';
import { createPerformanceLogger } from 'src/utils/performance-logger';
import { blockGridToBlockList } from '../helpers/block/block-grid-to-block-list';
import { blocksToBlockGrid } from '../helpers/block/blocks-to-block-grid';
import { findBlock } from '../helpers/find-block';
import { generateBlockId } from '../helpers/generate-block-id';
import { getSuperRect } from '../helpers/get-super-rect';
import {
  AspectRatio,
  AutoHeightSize,
  Block,
  BlockContent,
  BlockGrid,
  BlockLayoutSpec,
  BlockVerticalAlignment,
  Boundary,
  BoundaryParams,
  BoundarySpace,
  CalculatedHeightSize,
  Rect,
  RenderElement,
  Section,
  SectionGrid,
  Size,
  StaticSize,
} from '../types';
import { calculateWidth } from './calculate-width';
import { getWidthPercentage } from './get-width-percentage';
import { locateBlockInGrid } from './locate-block-in-grid';

export const DEFAULT_LAYOUT_CONFIG = {
  margin: {
    top: 80,
    bottom: 240,
  },
  rowGap: 16,
  columnGap: 16,
  boundary: {
    thickness: 4,
    horizontalOffset: 8,
    verticalOffset: 8,
  },
  block: {
    maxHeight: 400,
    defaultHeight: 400,
    defaultAspectRatio: {
      width: 16,
      height: 9,
    },
    minWidth: 160,
  },
  row: {
    minHeight: 40,
  },
  section: {
    divider: {
      thickness: 3,
      margin: 80,
    },
  },
} as const;

export type LayoutConfig = typeof DEFAULT_LAYOUT_CONFIG;

function getDragPolygonHorizontal({
  isFirst,
  isLast,
  isFirstSection,
  isLastSection,
  rowY,
  previousRowHeight,
  rowHeight,
  inputs,
  outputs,
  layoutConfig,
}: {
  isFirst: boolean;
  isLast: boolean;
  isFirstSection: boolean;
  isLastSection: boolean;
  rowY: number;
  previousRowHeight: number;
  rowHeight: number;
  outputs: LayoutOutputs;
  inputs: LayoutInputs;
  layoutConfig: LayoutConfig;
}): NonNullable<BoundarySpace['dragPolygon']> {
  const left = -inputs.outerAreaLeftOffset;
  const right = outputs.gridWidth + inputs.outerAreaRightOffset;
  const top =
    isFirstSection && isFirst
      ? 0
      : isFirst
      ? rowY - layoutConfig.section.divider.margin - layoutConfig.section.divider.thickness / 2
      : rowY - layoutConfig.rowGap - previousRowHeight / 2;
  const bottom =
    isLastSection && isLast
      ? rowY - layoutConfig.rowGap + outputs.bottomOffset
      : isLast
      ? rowY - layoutConfig.rowGap + layoutConfig.section.divider.margin
      : rowY + rowHeight / 2;

  // console.log('getDragPolygonHorizontal', 'left', left, 'right', right, 'top', top, 'bottom', bottom);
  return [
    [left, top],
    [right, top],
    [right, bottom],
    [left, bottom],
  ];
}

function getDragPolygonVertical({
  isFirst,
  isLast,
  isFirstRow,
  isLastRow,
  isFirstSection,
  isLastSection,
  rowY,
  rowHeight,
  colX,
  colWidth,
  previousColWidth,
  inputs,
  outputs,
  layoutConfig,
}: {
  isFirst: boolean;
  isLast: boolean;
  isFirstRow: boolean;
  isLastRow: boolean;
  isFirstSection: boolean;
  isLastSection: boolean;
  rowY: number;
  rowHeight: number;
  colX: number;
  colWidth: number;
  previousColWidth: number;
  outputs: LayoutOutputs;
  inputs: LayoutInputs;
  layoutConfig: LayoutConfig;
}): NonNullable<BoundarySpace['dragPolygon']> {
  if (isFirst) {
    const leftOuter = -inputs.outerAreaLeftOffset;
    const topOuter =
      isFirstSection && isFirstRow
        ? 0
        : isFirstRow
        ? rowY - layoutConfig.section.divider.margin
        : rowY - layoutConfig.rowGap / 2;
    const leftInner = -layoutConfig.columnGap / 2;
    const topInner = rowY - layoutConfig.rowGap / 2;
    const right = colX + colWidth / 2;
    const verticalCenter = rowY + rowHeight / 2;
    const bottomInner = rowY + rowHeight + layoutConfig.rowGap / 2;
    const bottomOuter = rowY + rowHeight + (isLastRow ? outputs.bottomOffset : layoutConfig.rowGap / 2);
    return [
      [leftOuter, topOuter],
      [leftInner, topInner],
      [right, verticalCenter],
      [leftInner, bottomInner],
      [leftOuter, bottomOuter],
    ];
  }
  if (isLast) {
    const left = colX - layoutConfig.columnGap - previousColWidth / 2;
    const rightInner = colX - layoutConfig.columnGap / 2;
    const rightOuter = colX - layoutConfig.columnGap + inputs.outerAreaRightOffset;
    const topOuter =
      isFirstSection && isFirstRow
        ? 0
        : isFirstRow
        ? rowY - layoutConfig.section.divider.margin
        : rowY - layoutConfig.rowGap / 2;
    const topInner = rowY - layoutConfig.rowGap / 2;
    const bottomInner = rowY + rowHeight + layoutConfig.rowGap / 2;
    const bottomOuter = rowY + rowHeight + (isLastRow ? outputs.bottomOffset : layoutConfig.rowGap / 2);
    const verticalCenter = rowY + rowHeight / 2;
    return [
      [left, verticalCenter],
      [rightInner, topInner],
      [rightOuter, topOuter],
      [rightOuter, bottomOuter],
      [rightInner, bottomInner],
    ];
  }
  const left = colX - layoutConfig.columnGap - previousColWidth / 2;
  const right = colX + colWidth / 2;
  const top = rowY - layoutConfig.rowGap / 2;
  const bottom = rowY + rowHeight + layoutConfig.rowGap / 2;
  const verticalCenter = rowY + rowHeight / 2;
  const horizontalCenter = colX - layoutConfig.columnGap / 2;

  return [
    [left, verticalCenter],
    [horizontalCenter, top],
    [right, verticalCenter],
    [horizontalCenter, bottom],
  ];
}

function calculateRowBlockSizes(
  row: Block[],
  rowWidth: number,
  layoutConfig: LayoutConfig
): { height: number; blockSizes: Size[] } {
  if (row.length > 1) {
    rowWidth -= layoutConfig.columnGap * (row.length - 1);
  }

  let blockSizes: Size[] = [];
  let rowHeight = 0;
  for (let i = 0; i < row.length; i++) {
    const block = row[i];
    const spec = block.layout.spec;
    let width = 0;
    let height = 0;
    if (spec.type === 'static') {
      width = calculateWidth(spec.width, rowWidth);
      height = spec.height || layoutConfig.block.defaultHeight;
    } else if (spec.type === 'auto-height') {
      width = calculateWidth(spec.width, rowWidth);
      height =
        spec.height || Math.min(Math.floor((spec.aspectRatio.height / spec.aspectRatio.width) * width), spec.maxHeight);
    } else if (spec.type === 'calculated-height') {
      width = calculateWidth(spec.width, rowWidth);
      height = Math.max(spec.height || spec.minHeight, spec.contentSize?.height || 0);
    }
    rowHeight = Math.max(rowHeight, height);
    blockSizes.push({ width, height });
  }
  return { height: rowHeight, blockSizes: blockSizes.map((bs) => ({ ...bs, height: rowHeight })) };
}

function createPlaceholderBlockLayout(width: StaticSize['width']): BlockLayoutSpec {
  return {
    spec: {
      type: 'static',
      width,
      height: null,
    },
    verticalAlign: 'center',
  };
}

function createTextBlockLayout(width: CalculatedHeightSize['width']): BlockLayoutSpec {
  return {
    spec: {
      type: 'calculated-height',
      width,
      height: null,
      minHeight: 0,
      contentSize: null,
    },
    verticalAlign: 'center',
  };
}

function createImageBlockLayout(
  width: AutoHeightSize['width'],
  {
    aspectRatio,
    maxHeight,
  }: {
    aspectRatio: AspectRatio;
    maxHeight: AutoHeightSize['maxHeight'];
  }
): BlockLayoutSpec {
  return {
    spec: {
      type: 'auto-height',
      width,
      height: null,
      maxHeight,
      aspectRatio,
    },
    verticalAlign: 'center',
  };
}

export type BlockLayoutExtraParams = Partial<
  Omit<StaticSize, 'type' | 'width'> & Omit<AutoHeightSize, 'type'> & Omit<CalculatedHeightSize, 'type'>
>;

function createBlockLayout(
  type: BlockContent['type'],
  width: BlockLayoutSpec['spec']['width'],
  params: BlockLayoutExtraParams,
  layoutConfig: LayoutConfig
): Block['layout'] {
  let blockLayout: Block['layout'];
  if (type === 'placeholder') {
    blockLayout = createPlaceholderBlockLayout(width);
  } else if (type === 'text') {
    blockLayout = createTextBlockLayout(width);
  } else if (type === 'image') {
    blockLayout = createImageBlockLayout(width, {
      aspectRatio: params.aspectRatio || layoutConfig.block.defaultAspectRatio,
      maxHeight: params.maxHeight || layoutConfig.block.maxHeight,
    });
  }
  return blockLayout!;
}

export type LayoutInputs = {
  innerAreaWidth: number;
  innerAreaHeight: number;
  outerAreaLeftOffset: number;
  outerAreaRightOffset: number;
};

export type LayoutOutputs = {
  gridWidth: number;
  gridHeight: number;
  topOffset: number;
  bottomOffset: number;
  innerActualWidth: number;
  innerActualHeight: number;
  sectionRects: Rect[];
};

export class LayoutManager extends EventEmitter {
  sections: Section[] = [];
  sectionGrids: SectionGrid[] = [];
  renderElements: RenderElement[] = [];
  // blockRects: Record<Block['id'], Rect> = {};
  layoutConfig: LayoutConfig;
  boundaries: Boundary[] = [];
  inputs: Nullable<LayoutInputs> = null;
  outputs: Nullable<LayoutOutputs> = null;
  constructor(layoutConfig: LayoutConfig = DEFAULT_LAYOUT_CONFIG) {
    super();
    this.layoutConfig = layoutConfig;
  }
  initializeSections(sections: Section[]) {
    this.sections = sections;
    this._updateSectionGrids();
    this._invalidate();
  }
  reset() {
    this.sections = [];
    this.sectionGrids = [];
    this.renderElements = [];
    this.boundaries = [];
    this.outputs = null;
  }
  setLayoutInputs(inputs: LayoutInputs) {
    // console.log('lm setLayoutInputs', inputs);
    this.inputs = inputs;
    this._updateSectionGrids();
    this._invalidate();
  }
  setBlockContentSize(id: Block['id'], size: Nullable<Size>) {
    // console.log('lm setBlockContentSize', { ...size });
    this.sections = produce(this.sections, (sections) => {
      const block = findBlock(sections, id);
      if (block && block.layout.spec.type === 'calculated-height') {
        block.layout.spec.contentSize = size;
      }
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  appendSection({
    sectionId,
    newBlockParams,
  }: {
    sectionId: Section['id'];
    newBlockParams: {
      blockId: Block['id'];
      blockContent: BlockContent;
      layoutExtraParams: BlockLayoutExtraParams;
    };
  }) {
    // console.log('lm appendSection');
    this.sections = produce(this.sections, (sections) => {
      sections.push({
        id: sectionId,
        name: null,
        blocks: [
          {
            id: newBlockParams.blockId,
            content: newBlockParams.blockContent,
            layout: createBlockLayout(
              newBlockParams.blockContent.type,
              '100%',
              newBlockParams.layoutExtraParams,
              this.layoutConfig
            ),
          },
        ],
      });
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  insertSectionAtBoundary({
    sectionId,
    boundary,
    newBlockParams,
  }: {
    sectionId: Section['id'];
    boundary: Boundary;
    newBlockParams: {
      blockId: Block['id'];
      blockContent: BlockContent;
      layoutExtraParams: BlockLayoutExtraParams;
    };
  }) {
    // console.log('lm insertSectionAtBoundary');
    if (boundary.orientation === 'vertical') {
      return;
    }
    this.sectionGrids = produce(this.sectionGrids, (sectionGrids) => {
      const { sectionIndex, insertionRow } = boundary;
      const { blockGrid } = sectionGrids[sectionIndex];
      if (insertionRow === 0) {
        sectionGrids.splice(sectionIndex, 0, {
          id: sectionId,
          name: null,
          blockGrid: [
            [
              {
                id: newBlockParams.blockId,
                content: newBlockParams.blockContent,
                layout: createBlockLayout(
                  newBlockParams.blockContent.type,
                  '100%',
                  newBlockParams.layoutExtraParams,
                  this.layoutConfig
                ),
              },
            ],
          ],
        });
        return;
      }
      if (insertionRow === blockGrid.length) {
        sectionGrids.splice(sectionIndex + 1, 0, {
          id: sectionId,
          name: null,
          blockGrid: [
            [
              {
                id: newBlockParams.blockId,
                content: newBlockParams.blockContent,
                layout: createBlockLayout(
                  newBlockParams.blockContent.type,
                  '100%',
                  newBlockParams.layoutExtraParams,
                  this.layoutConfig
                ),
              },
            ],
          ],
        });
        return;
      }
      const remainingRows = blockGrid.splice(insertionRow, blockGrid.length - insertionRow);
      sectionGrids.splice(
        sectionIndex + 1,
        0,
        {
          name: null,
          id: sectionId,
          blockGrid: [
            [
              {
                id: newBlockParams.blockId,
                content: newBlockParams.blockContent,
                layout: createBlockLayout(
                  newBlockParams.blockContent.type,
                  '100%',
                  newBlockParams.layoutExtraParams,
                  this.layoutConfig
                ),
              },
            ],
          ],
        },
        {
          name: null,
          id: generateBlockId(),
          blockGrid: remainingRows,
        }
      );
    });
    this._updateSections();
    this._invalidate();
  }
  uninsertSectionAtBoundary({
    sectionId,
    boundary,
    joinSections,
  }: {
    sectionId: Section['id'];
    boundary: Boundary;
    joinSections: boolean;
  }) {
    this.sections = produce(this.sections, (sections) => {
      const index = sections.findIndex((s) => s.id === sectionId);
      if (index === -1) {
        throw new Error('Section not found');
      }
      if (joinSections) {
        const originalSection = sections[index - 1];
        originalSection.blocks.splice(originalSection.blocks.length, 0, ...sections[index + 1].blocks);
        sections.splice(index, 2);
      } else {
        sections.splice(index, 1);
      }
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  moveSectionDividerToBoundary({
    previousSectionId,
    nextSectionId,
    boundaryParams,
  }: {
    previousSectionId: Nullable<Section['id']>;
    nextSectionId: Nullable<Section['id']>;
    boundaryParams: BoundaryParams;
  }) {
    // console.log('lm moveSectionDividerToBoundary');
    let { sectionIndex: newSectionIndex, insertionRow, orientation } = boundaryParams;
    if (orientation === 'vertical') {
      return;
    }
    this.sectionGrids = produce(this.sectionGrids, (sectionGrids) => {
      let currentAboveSectionIndex = sectionGrids.findIndex((s) => s.id === previousSectionId);
      let currentBelowSectionIndex = sectionGrids.findIndex((s) => s.id === nextSectionId);
      if (currentAboveSectionIndex === -1 || currentBelowSectionIndex === -1) {
        return;
      }

      const sectionToExtendIndex =
        newSectionIndex >= currentBelowSectionIndex ? currentAboveSectionIndex : currentBelowSectionIndex;
      const sectionToExtend = sectionGrids[sectionToExtendIndex];

      if (newSectionIndex >= currentBelowSectionIndex) {
        let i = sectionToExtendIndex + 1;
        let j = i;
        while (i < newSectionIndex) {
          sectionToExtend.blockGrid.splice(sectionToExtend.blockGrid.length, 0, ...sectionGrids[j].blockGrid);
          sectionGrids.splice(j, 1);
          i++;
        }
        sectionToExtend.blockGrid.splice(
          sectionToExtend.blockGrid.length,
          0,
          ...sectionGrids[j].blockGrid.slice(0, insertionRow)
        );
        sectionGrids[j].blockGrid.splice(0, insertionRow);
        if (j < sectionGrids.length - 1 && sectionGrids[j].blockGrid.length === 0) {
          sectionGrids.splice(j, 1);
        }
      } else {
        let i = sectionToExtendIndex - 1;
        let j = i;
        while (i > newSectionIndex) {
          sectionToExtend.blockGrid.splice(sectionToExtend.blockGrid.length, 0, ...sectionGrids[j].blockGrid);
          sectionGrids.splice(j, -1);
          i--;
        }
        sectionToExtend.blockGrid.splice(0, 0, ...sectionGrids[j].blockGrid.slice(insertionRow));
        sectionGrids[j].blockGrid.splice(insertionRow, sectionGrids[j].blockGrid.length - insertionRow);

        if (j > 0 && sectionGrids[j].blockGrid.length === 0) {
          sectionGrids.splice(j, 1);
        }
      }
    });

    this._updateSections();
    this._invalidate();
  }
  moveSection({ sectionId, newSectionIndex }: { sectionId: Section['id']; newSectionIndex: number }) {
    // console.log('lm moveSection');
    this.sections = produce(this.sections, (sections) => {
      const sectionIndex = sections.findIndex((s) => s.id === sectionId);
      if (sectionIndex === -1) {
        console.warn(`Section with id ${sectionId} not found`);
        return;
      }
      const section = sections.splice(sectionIndex, 1)[0];
      sections.splice(newSectionIndex, 0, section);
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  mergeSections({
    previousSectionId,
    nextSectionId,
  }: {
    previousSectionId: Section['id'];
    nextSectionId: Section['id'];
  }) {
    // console.log('lm mergeSections');
    const { sectionGrids } = this;
    this.sectionGrids = produce(sectionGrids, (sectionGrids) => {
      const previousSectionIndex = sectionGrids.findIndex((sg) => sg.id === previousSectionId);
      const nextSectionIndex = sectionGrids.findIndex((sg) => sg.id === nextSectionId);
      if (previousSectionIndex === -1 || nextSectionIndex === -1) {
        throw new Error('Section not found');
      }
      const previousSection = sectionGrids[previousSectionIndex];
      const nextSection = sectionGrids[nextSectionIndex];
      previousSection.blockGrid.splice(previousSection.blockGrid.length, 0, ...nextSection.blockGrid);
      sectionGrids.splice(nextSectionIndex, 1);
    });
    this._updateSections();
    this._invalidate();
  }
  unmergeSections({
    previousSectionId,
    previousSectionBlocks,
    nextSection,
  }: {
    previousSectionId: Section['id'];
    previousSectionBlocks: Block[];
    nextSection: Section;
  }) {
    // console.log('lm unmergeSections');
    const { sections } = this;
    this.sections = produce(sections, (sections) => {
      const previousSectionIndex = sections.findIndex((s) => s.id === previousSectionId);
      if (previousSectionIndex === -1) {
        throw new Error('Section not found');
      }
      sections[previousSectionIndex].blocks = previousSectionBlocks;
      sections.splice(previousSectionIndex + 1, 0, nextSection);
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  appendBlock({
    blockId,
    blockContent,
    layoutExtraParams,
    sectionId,
  }: {
    blockId: Block['id'];
    blockContent: BlockContent;
    layoutExtraParams: BlockLayoutExtraParams;
    sectionId?: Section['id'];
  }) {
    // console.log('lm appendBlock');
    const { sections, sectionGrids, layoutConfig } = this;
    if (!sectionId) {
      sectionId = sections[sections.length - 1].id;
    }
    this.sectionGrids = produce(sectionGrids, (sectionGrids) => {
      const sectionGrid = sectionGrids.find((sg) => sg.id === sectionId);
      if (!sectionGrid) {
        throw new Error('Section not found');
      }
      const { blockGrid } = sectionGrid;
      sectionGrid.blockGrid = produce(blockGrid, (grid) => {
        if (grid.length > 0) {
          const lastRow = grid[grid.length - 1];
          if (lastRow.length === 1) {
            let [existingBlock] = lastRow;
            if (existingBlock.content.type === 'text' && blockContent.type === 'text') {
              // Don't need 2 text blocks next to each other by default
              grid.push([
                {
                  id: blockId,
                  content: blockContent,
                  layout: createBlockLayout(blockContent.type, '100%', layoutExtraParams, layoutConfig),
                },
              ]);
              return;
            }

            existingBlock.layout.spec.width = '50%';
            lastRow.push({
              id: blockId,
              content: blockContent,
              layout: createBlockLayout(blockContent.type, '50%', layoutExtraParams, layoutConfig),
            });
            return;
          }
        }
        grid.push([
          {
            id: blockId,
            content: blockContent,
            layout: createBlockLayout(blockContent.type, '100%', layoutExtraParams, layoutConfig),
          },
        ]);
      });
    });

    this._updateSections();
    this._invalidate();
  }
  replaceBlock({
    blockId,
    blockContent,
    layoutExtraParams,
  }: {
    blockId: Block['id'];
    blockContent: BlockContent;
    layoutExtraParams: BlockLayoutExtraParams;
  }) {
    // console.log('lm replaceBlock');
    const { outputs, sectionGrids, layoutConfig } = this;
    if (!outputs) {
      return;
    }
    this.sectionGrids = produce(sectionGrids, (sectionGrids) => {
      for (let sectionGrid of sectionGrids) {
        const { blockGrid } = sectionGrid;
        sectionGrid.blockGrid = produce(blockGrid, (grid) => {
          for (let i = 0; i < grid.length; i++) {
            const row = grid[i];
            const blockIndex = row.findIndex((b) => b.id === blockId);
            if (blockIndex === -1) {
              continue;
            }
            const block = row[blockIndex];
            if (row.length === 1) {
              block.content = blockContent;
              block.layout = createBlockLayout(blockContent.type, '100%', layoutExtraParams, layoutConfig);
            } else if (row.length === 2) {
              const otherBlock = row[1 - blockIndex];
              const otherBlockWidthPercentage = getWidthPercentage(otherBlock.layout.spec.width, outputs.gridWidth);
              block.content = blockContent;
              block.layout = createBlockLayout(
                blockContent.type,
                `${100 - otherBlockWidthPercentage}%`,
                layoutExtraParams,
                layoutConfig
              );
            }
          }
        });
      }
    });
    this._updateSections();
    this._invalidate();
  }
  updateSection(sectionId: Section['id'], updatedSection: Pick<Section, 'name'>) {
    // console.log('lm updateSection');
    this.sections = produce(this.sections, (sections) => {
      const section = sections.find((s) => s.id === sectionId);
      if (section) {
        section.name = updatedSection.name;
      }
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  updateBlockContent(id: Block['id'], updatedContent: Partial<BlockContent>) {
    // console.log('lm updateBlockContent');
    this.sections = produce(this.sections, (sections) => {
      const block = findBlock(sections, id);
      if (block) {
        block.content = Object.assign({}, block.content, updatedContent);
      }
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  insertBlockAtBoundary({
    blockId,
    blockContent,
    layoutExtraParams,
    boundary,
  }: {
    blockId: Block['id'];
    blockContent: BlockContent;
    layoutExtraParams: BlockLayoutExtraParams;
    boundary: Boundary;
  }) {
    console.log('lm insertBlockAtBoundary', blockId, blockContent, layoutExtraParams, boundary);
    const performanceLogger = createPerformanceLogger('insertBlockAtBoundary');
    const { sectionGrids, outputs, layoutConfig } = this;
    if (!outputs) {
      return;
    }
    this.sectionGrids = produce(sectionGrids, (sectionGrids) => {
      const sectionGrid = sectionGrids[boundary.sectionIndex];
      const { blockGrid } = sectionGrid;
      sectionGrid.blockGrid = produce(blockGrid, (grid) => {
        let { orientation, insertionRow, insertionCol } = boundary;

        if (insertionRow > grid.length) {
          console.warn('insertionRow too high');
          insertionRow = grid.length;
        }
        if (insertionRow === grid.length) {
          grid.push([
            {
              id: blockId,
              content: blockContent,
              layout: createBlockLayout(blockContent.type, '100%', layoutExtraParams, layoutConfig),
            },
          ]);
          return;
        }
        if (orientation === 'horizontal') {
          grid.splice(insertionRow, 0, [
            {
              id: blockId,
              content: blockContent,
              layout: createBlockLayout(blockContent.type, '100%', layoutExtraParams, layoutConfig),
            },
          ]);
          return;
        }
        const row = grid[insertionRow];
        if (insertionCol > row.length) {
          console.warn('insertionCol too high');
          insertionCol = row.length;
        }
        if (row.length === 0) {
          grid[insertionRow].push({
            id: blockId,
            content: blockContent,
            layout: createBlockLayout(blockContent.type, '100%', layoutExtraParams, layoutConfig),
          });
          return;
        }
        if (row.length === 1) {
          const [existingBlock] = row;
          existingBlock.layout.spec.width = '50%';
          row.splice(insertionCol, 0, {
            id: blockId,
            content: blockContent,
            layout: createBlockLayout(blockContent.type, '50%', layoutExtraParams, layoutConfig),
          });
          return;
        }
        if (row.length === 2) {
          if (insertionCol === 0 || insertionCol === 1) {
            const [firstBlock, lastBlock] = row;
            const otherBlockWidthPercentage = getWidthPercentage(firstBlock.layout.spec.width, outputs.gridWidth);
            row.splice(insertionCol, 0, {
              id: blockId,
              content: blockContent,
              layout: createBlockLayout(
                blockContent.type,
                `${100 - otherBlockWidthPercentage}%`,
                layoutExtraParams,
                layoutConfig
              ),
            });
            row.pop();
            lastBlock.layout.spec.width = `100%`;
            grid.splice(insertionRow + 1, 0, [lastBlock]);
          } else {
            grid.splice(insertionRow + 1, 0, [
              {
                id: blockId,
                content: blockContent,
                layout: createBlockLayout(blockContent.type, '100%', layoutExtraParams, layoutConfig),
              },
            ]);
          }
          return;
        }
      });
    });
    performanceLogger('updated sectionGrids');

    this._updateSections();
    performanceLogger('updated sections');
    this._invalidate();
    performanceLogger('invalidated');
  }
  moveBlockToBoundary(id: Block['id'], boundaryParams: BoundaryParams) {
    // console.log('lm moveBlockToBoundary');
    const { outputs } = this;
    if (!outputs) {
      return;
    }
    const block = findBlock(this.sections, id);
    if (!block) {
      return;
    }
    const blockLocation = locateBlockInGrid(this.sectionGrids, id);
    if (!blockLocation) {
      return;
    }

    let {
      orientation,
      sectionIndex: newSectionIndex,
      insertionRow: newRowIndex,
      insertionCol: newColIndex,
    } = boundaryParams;
    const { rowIndex: initialRowIndex, colIndex: initialColIndex, sectionIndex: initialSectionIndex } = blockLocation;

    this.sectionGrids = produce(this.sectionGrids, (sectionGrids) => {
      const initialBlockGrid = sectionGrids[initialSectionIndex].blockGrid;
      const initialRow = initialBlockGrid[initialRowIndex];
      const newBlockGrid = sectionGrids[newSectionIndex].blockGrid;
      const newRow = newBlockGrid[newRowIndex];
      const block = initialRow[initialColIndex];

      const addBlockToNewRow = () => {
        block.layout.spec.width = '100%';
        newBlockGrid.splice(newRowIndex, 0, [block]);
      };
      const removeInitialRow = () => {
        initialBlockGrid.splice(initialRowIndex, 1);
      };
      const removeBlockFromInitialRow = () => {
        const otherBlock = initialRow[1 - initialColIndex];
        otherBlock.layout.spec.width = '100%';
        initialRow.splice(initialColIndex, 1);
      };
      const removeBlockOrRow = () => {
        if (initialRow.length === 1) {
          removeInitialRow();
        } else {
          removeBlockFromInitialRow();
        }
      };
      const addBlockToRowVertical = () => {
        if (newRow.length === 1) {
          const [otherBlock] = newRow;
          otherBlock.layout.spec.width = `50%`;
          block.layout.spec.width = `50%`;
          newRow.splice(newColIndex, 0, block);
        } else if (newRow.length === 2) {
          if (newColIndex === 0 || newColIndex === 1) {
            const [firstBlock] = newRow;
            const otherBlockWidthPercentage = getWidthPercentage(firstBlock.layout.spec.width, outputs.gridWidth);
            newRow.splice(newColIndex, 0, block);
            const lastBlock = newRow[2];
            newRow.pop();
            lastBlock.layout.spec.width = `100%`;
            newBlockGrid.splice(newRowIndex + 1, 0, [lastBlock]);
            block.layout.spec.width = `${100 - otherBlockWidthPercentage}%`;
          } else {
            newBlockGrid.splice(newRowIndex + 1, 0, [block]);
            block.layout.spec.width = '100%';
          }
        }
      };

      if (initialSectionIndex !== newSectionIndex) {
        if (orientation === 'horizontal') {
          addBlockToNewRow();
          removeBlockOrRow();
          return;
        }
        if (orientation === 'vertical') {
          addBlockToRowVertical();
          removeBlockOrRow();
          return;
        }
      } else {
        if (orientation === 'horizontal') {
          if (newRowIndex === initialRowIndex) {
            if (initialRow.length === 1) {
              return;
            }
            removeBlockFromInitialRow();
            addBlockToNewRow();
            return;
          }
          if (newRowIndex < initialRowIndex) {
            removeBlockOrRow();
            addBlockToNewRow();
            return;
          }
          if (newRowIndex > initialRowIndex) {
            addBlockToNewRow();
            removeBlockOrRow();
            return;
          }
        }
        if (orientation === 'vertical') {
          if (newRowIndex === initialRowIndex) {
            if (initialRow.length === 1) {
              return;
            }
            if (newColIndex === initialColIndex) {
              return;
            }
            if (newColIndex < initialColIndex) {
              initialRow.splice(initialColIndex, 1);
              initialRow.splice(newColIndex, 0, block);
              return;
            }
            if (newColIndex > initialColIndex) {
              initialRow.splice(newColIndex, 0, block);
              initialRow.splice(initialColIndex, 1);
              return;
            }
          }

          if (newRowIndex < initialRowIndex) {
            if (newRow.length === 0) {
              return;
            }
            removeBlockOrRow();
            addBlockToRowVertical();
            return;
          }
          if (newRowIndex > initialRowIndex) {
            if (newRow.length === 0) {
              return;
            }
            addBlockToRowVertical();
            removeBlockOrRow();
            return;
          }
        }
      }
    });
    this._updateSections();
    this._invalidate();
  }
  setBlockVerticalAlignment(id: Block['id'], verticalAlignment: BlockVerticalAlignment) {
    // console.log('lm setBlockVerticalAlignment');
    this.sections = produce(this.sections, (sections) => {
      const block = findBlock(sections, id);
      if (block) {
        block.layout.verticalAlign = verticalAlignment;
      }
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  resizeHorizontal(boundary: Boundary, boundaryX: number) {
    // console.log('lm resizeHorizontal', boundary, boundaryX);
    const { outputs, layoutConfig } = this;
    if (!outputs) {
      return;
    }
    const leftBlockWidth = clamp(
      boundaryX - layoutConfig.columnGap / 2,
      layoutConfig.block.minWidth,
      outputs.innerActualWidth - layoutConfig.columnGap - layoutConfig.block.minWidth
    );
    const { sectionIndex, insertionRow: boundaryRowIndex, insertionCol: boundaryColIndex } = boundary;
    this.sectionGrids = produce(this.sectionGrids, (sectionGrids) => {
      const { blockGrid } = sectionGrids[sectionIndex];
      if (boundaryRowIndex >= blockGrid.length) {
        console.warn('insertionRow too high');
        return;
      }
      const row = blockGrid[boundaryRowIndex];
      if (boundaryColIndex >= row.length) {
        console.warn('insertionCol too high');
        return;
      }
      if (row.length < 2) {
        return;
      }
      const [firstBlock, secondBlock] = row;
      const percent = clamp((leftBlockWidth / (outputs.innerActualWidth - layoutConfig.columnGap)) * 100, 2, 98);
      firstBlock.layout.spec.width = `${percent}%`;
      secondBlock.layout.spec.width = `${100 - percent}%`;
    });
    this._updateSections();
    this._invalidate();
  }
  resizeVertical(boundary: Boundary, bottomBoundaryY: number) {
    // console.log('lm resizeVertical', boundary, bottomBoundaryY);
    const { outputs, renderElements, layoutConfig } = this;
    if (!outputs) {
      return;
    }
    const blockRects = renderElements.reduce((blockRects, renderElement) => {
      if (renderElement.type === 'block') {
        blockRects[renderElement.block.id] = renderElement.rect;
      }
      return blockRects;
    }, {} as Record<string, Rect>);
    const { sectionIndex, insertionRow } = boundary;
    const rowIndex = insertionRow - 1;
    this.sectionGrids = produce(this.sectionGrids, (sectionGrids) => {
      const { blockGrid } = sectionGrids[sectionIndex];
      if (rowIndex >= blockGrid.length) {
        console.warn('row too high');
        return;
      }
      if (rowIndex < 0) {
        console.warn('row too low');
        return;
      }
      const row = blockGrid[rowIndex];
      if (row.length === 0) {
        return;
      }
      const rowRect = getSuperRect(row.map((block) => blockRects[block.id]!));
      const newRowHeight = bottomBoundaryY - rowRect.y - layoutConfig.rowGap / 2;
      // console.log('lm resizeVertical', newRowHeight);
      row.forEach((block) => {
        if (block.layout.spec.type === 'static') {
          block.layout.spec.height = Math.max(newRowHeight, layoutConfig.row.minHeight);
          return;
        } else if (block.layout.spec.type === 'auto-height') {
          block.layout.spec.height = Math.max(newRowHeight, layoutConfig.row.minHeight);
          return;
        } else if (block.layout.spec.type === 'calculated-height') {
          block.layout.spec.height = Math.max(
            newRowHeight,
            block.layout.spec.contentSize?.height || 0,
            layoutConfig.row.minHeight
          );
          return;
        }
      });
    });
    this._updateSections();
    this._invalidate();
  }
  deleteBlock(id: Block['id']) {
    // console.log('lm deleteBlock');
    this.sectionGrids = produce(this.sectionGrids, (sectionGrids) => {
      const result = locateBlockInGrid(sectionGrids, id);
      if (!result) {
        return;
      }
      const { sectionIndex } = result;
      const sectionGrid = sectionGrids[sectionIndex];
      sectionGrid.blockGrid = produce(sectionGrid.blockGrid, (grid) => {
        for (let i = 0; i < grid.length; i++) {
          const row = grid[i];
          const index = row.findIndex((b) => b.id === id);
          if (index !== -1) {
            if (row.length === 2) {
              row.splice(index, 1);
              row[0].layout.spec.width = '100%';
            } else if (row.length === 1) {
              grid.splice(i, 1);
            }
            return;
          }
        }
      });
    });
    this._updateSections();
    this._invalidate();
  }
  deleteSection(id: Section['id']) {
    // console.log('lm deleteSection');
    this.sectionGrids = produce(this.sectionGrids, (sectionGrids) => {
      const index = sectionGrids.findIndex((sectionGrid) => sectionGrid.id === id);
      if (index === -1) {
        return;
      }
      sectionGrids.splice(index, 1);
    });
    this._updateSections();
    this._invalidate();
  }
  undeleteSection(section: Section, sectionIndex: number) {
    // console.log('lm undeleteSection');
    this.sections = produce(this.sections, (sections) => {
      sections.splice(sectionIndex, 0, section);
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  _updateSectionGrids() {
    const { inputs } = this;
    if (!inputs) {
      return;
    }
    this.sectionGrids = this.sections.map((section) => ({
      id: section.id,
      name: section.name,
      blockGrid: blocksToBlockGrid(section.blocks, inputs.innerAreaWidth, 'web'),
    }));
  }
  _updateSections() {
    this.sections = this.sectionGrids.map((sectionGrid) => ({
      id: sectionGrid.id,
      name: sectionGrid.name,
      blocks: blockGridToBlockList(sectionGrid.blockGrid),
    }));
  }
  _invalidate() {
    this._layout();
    this._updateBoundaries();
    this.emit('update');
  }
  _layout() {
    const { sectionGrids, inputs } = this;
    if (!inputs) {
      return;
    }
    // console.log('lm _layout');
    const {
      rowGap: GRID_ROW_GAP,
      columnGap: GRID_COLUMN_GAP,
      margin: { top: MARGIN_TOP, bottom: MARGIN_BOTTOM },
      section: {
        divider: { margin: SECTION_DIVIDER_MARGIN, thickness: SECTION_DIVIDER_THICKNESS },
      },
    } = this.layoutConfig;
    const { innerAreaWidth, innerAreaHeight } = inputs;

    const gridWidth = innerAreaWidth;

    let x = 0;
    let y = 0;
    let gridHeight = 0;

    let renderElements: RenderElement[] = [];
    let sectionRects: Rect[] = [];

    for (let k = 0; k < sectionGrids.length; k++) {
      if (k > 0) {
        y += SECTION_DIVIDER_MARGIN;
        gridHeight += SECTION_DIVIDER_MARGIN;
      }
      const sectionGrid = sectionGrids[k];
      const blockGrid = sectionGrid.blockGrid;
      let sectionHeight = 0;
      let sectionY = y;
      const isEmptyBlockGrid = blockGrid.length === 0;
      for (let i = 0; i < blockGrid.length; i++) {
        const row = blockGrid[i];
        const { height: rowHeight, blockSizes } = calculateRowBlockSizes(row, gridWidth, this.layoutConfig);
        for (let j = 0; j < row.length; j++) {
          const block = row[j];
          const blockSize = blockSizes[j]!;
          renderElements.push({
            type: 'block',
            block,
            rect: { x, y, width: blockSize.width, height: blockSize.height },
          });
          x += blockSize.width + GRID_COLUMN_GAP;
        }
        sectionHeight += rowHeight;
        y += rowHeight;
        if (i < blockGrid.length - 1) {
          sectionHeight += GRID_ROW_GAP;
          y += GRID_ROW_GAP;
        }
        x = 0;
      }
      sectionRects.push({
        x: 0,
        y: sectionY,
        width: gridWidth,
        height: sectionHeight,
      });
      y += SECTION_DIVIDER_MARGIN + SECTION_DIVIDER_THICKNESS;
      gridHeight += sectionHeight;
      if (k < sectionGrids.length - 1) {
        gridHeight += SECTION_DIVIDER_MARGIN + SECTION_DIVIDER_THICKNESS;
        renderElements.push({
          type: 'divider',
          previousSectionId: sectionGrid.id,
          nextSectionId: sectionGrids[k + 1].id,
          rect: { x: 0, y: y - SECTION_DIVIDER_THICKNESS / 2, width: gridWidth, height: SECTION_DIVIDER_THICKNESS },
          isLast: false,
        });
      }
      if (k === sectionGrids.length - 1) {
        renderElements.push({
          type: 'divider',
          previousSectionId: sectionGrid.id,
          nextSectionId: null,
          rect: { x: 0, y: y - SECTION_DIVIDER_THICKNESS / 2, width: gridWidth, height: SECTION_DIVIDER_THICKNESS },
          isLast: true,
        });
      }
    }

    // console.log('lm _layout', 'gridHeight', gridHeight, 'innerAreaHeight', innerAreaHeight);

    const topOffset = Math.max(MARGIN_TOP, (innerAreaHeight - gridHeight) / 2);
    // console.log('lm _layout', 'topOffset', topOffset);
    const bottomOffset = Math.max(MARGIN_BOTTOM, (innerAreaHeight - gridHeight) / 2);
    // console.log('lm _layout', 'bottomOffset', bottomOffset);

    const innerActualHeight = gridHeight + topOffset + bottomOffset;
    // console.log('lm _layout', 'innerActualHeight', innerActualHeight);

    renderElements = produce(renderElements, (renderElements) => {
      for (const renderElement of renderElements) {
        renderElement.rect.y += topOffset;
      }
    });
    sectionRects = produce(sectionRects, (sectionRects) => {
      for (const sectionRect of sectionRects) {
        sectionRect.y += topOffset;
      }
    });
    this.outputs = {
      gridHeight,
      gridWidth,
      topOffset,
      bottomOffset,
      innerActualWidth: innerAreaWidth,
      innerActualHeight,
      sectionRects,
    };
    this.renderElements = renderElements;
  }
  _updateBoundaries() {
    const { renderElements, inputs, outputs } = this;
    if (!inputs || !outputs) {
      return;
    }
    // console.log('lm _updateBoundaries');
    const {
      rowGap: GRID_ROW_GAP,
      columnGap: GRID_COLUMN_GAP,
      boundary: {
        thickness: BOUNDARY_THICKNESS,
        horizontalOffset: BOUNDARY_H_OFFSET,
        verticalOffset: BOUNDARY_V_OFFSET,
      },
      section: {
        divider: { margin: SECTION_DIVIDER_MARGIN, thickness: SECTION_DIVIDER_THICKNESS },
      },
    } = this.layoutConfig;
    const { gridWidth, gridHeight, topOffset, sectionRects } = outputs;

    let boundaries: Boundary[] = [];
    let rowStartX: number = 0;
    let colStartX = 0;
    let previousRowHeight = 0;
    let y = 0;
    for (let k = 0; k < this.sectionGrids.length; k++) {
      const sectionGrid = this.sectionGrids[k];
      const blockGrid = sectionGrid.blockGrid;
      const blockRects = renderElements.reduce((blockRects, renderElement) => {
        if (renderElement.type === 'block') {
          blockRects[renderElement.block.id] = renderElement.rect;
        }
        return blockRects;
      }, {} as Record<string, Rect>);
      const isEmptyBlockGrid = blockGrid.length === 0;
      for (let i = 0; i < blockGrid.length; i++) {
        const row = blockGrid[i];
        const maxRowHeight = max(row.map((block) => blockRects[block.id]!.height)) || 0;
        const rowRect = getSuperRect(row.map((block) => blockRects[block.id]!));
        // boundary above each row
        boundaries.push({
          sectionIndex: k,
          orientation: 'horizontal',
          x: rowRect.x,
          y: rowRect.y - (i == 0 ? BOUNDARY_V_OFFSET : GRID_ROW_GAP / 2) - BOUNDARY_THICKNESS / 2,
          width: gridWidth,
          height: BOUNDARY_THICKNESS,
          insertionRow: i,
          insertionCol: 0,
          isFirstInRow: false,
          isLastInRow: false,
          isFirstInSection: i === 0,
          isLastInSection: i === blockGrid.length - 1,
          dragPolygon: getDragPolygonHorizontal({
            isFirst: i === 0,
            isLast: false,
            isFirstSection: k === 0,
            isLastSection: k === this.sectionGrids.length - 1,
            rowY: rowRect.y,
            previousRowHeight,
            rowHeight: maxRowHeight,
            inputs,
            outputs,
            layoutConfig: this.layoutConfig,
          }),
        });

        let previousColWidth = 0;
        for (let j = 0; j < row.length; j++) {
          const gridItem = row[j];
          const blockRect = blockRects[gridItem.id];
          const blockWidth = blockRect.width;
          // boundary before each column in a row
          boundaries.push({
            sectionIndex: k,
            orientation: 'vertical',
            x: colStartX - (j == 0 ? BOUNDARY_H_OFFSET : GRID_COLUMN_GAP / 2) - BOUNDARY_THICKNESS / 2,
            y: rowRect.y,
            width: BOUNDARY_THICKNESS,
            height: maxRowHeight,
            insertionRow: i,
            insertionCol: j,
            isFirstInRow: j === 0,
            isLastInRow: false,
            isFirstInSection: false,
            isLastInSection: false,
            dragPolygon: getDragPolygonVertical({
              isFirst: j === 0,
              isLast: false,
              isFirstRow: i === 0,
              isLastRow: i === blockGrid.length - 1,
              isFirstSection: k === 0,
              isLastSection: k === this.sectionGrids.length - 1,
              rowY: rowRect.y,
              rowHeight: maxRowHeight,
              colX: colStartX,
              colWidth: blockWidth,
              previousColWidth,
              inputs,
              outputs,
              layoutConfig: this.layoutConfig,
            }),
          });
          previousColWidth = blockWidth;
          colStartX += blockWidth + GRID_COLUMN_GAP;
        }
        // boundary at the end of each row
        boundaries.push({
          sectionIndex: k,
          orientation: 'vertical',
          x: colStartX - GRID_COLUMN_GAP + BOUNDARY_V_OFFSET - BOUNDARY_THICKNESS / 2,
          y: rowRect.y,
          width: BOUNDARY_THICKNESS,
          height: maxRowHeight,
          insertionRow: i,
          insertionCol: row.length,
          isFirstInRow: row.length === 0,
          isLastInRow: true,
          isFirstInSection: false,
          isLastInSection: false,
          dragPolygon: getDragPolygonVertical({
            isFirst: row.length === 0,
            isLast: true,
            isFirstRow: i === 0,
            isLastRow: i === blockGrid.length - 1,
            isFirstSection: k === 0,
            isLastSection: k === this.sectionGrids.length - 1,
            rowY: rowRect.y,
            rowHeight: maxRowHeight,
            colX: colStartX,
            colWidth: previousColWidth,
            previousColWidth,
            inputs,
            outputs,
            layoutConfig: this.layoutConfig,
          }),
        });
        previousColWidth = 0;
        previousRowHeight = maxRowHeight;
        rowStartX = 0;
        colStartX = 0;
        y = rowRect.y + maxRowHeight + GRID_ROW_GAP;
      }
      if (isEmptyBlockGrid) {
        y = sectionRects[k].y;
      }

      // boundary after the last row in a section
      boundaries.push({
        sectionIndex: k,
        orientation: 'horizontal',
        x: rowStartX,
        y: isEmptyBlockGrid ? y : y - GRID_ROW_GAP + BOUNDARY_V_OFFSET,
        width: gridWidth,
        height: BOUNDARY_THICKNESS,
        insertionRow: blockGrid.length,
        insertionCol: 0,
        isFirstInRow: false,
        isLastInRow: false,
        isFirstInSection: blockGrid.length === 0,
        isLastInSection: true,
        dragPolygon: getDragPolygonHorizontal({
          isFirst: blockGrid.length === 0,
          isLast: true,
          isFirstSection: k === 0,
          isLastSection: k === this.sectionGrids.length - 1,
          rowY: y,
          previousRowHeight,
          rowHeight: previousRowHeight,
          inputs,
          outputs,
          layoutConfig: this.layoutConfig,
        }),
      });
    }
    const lastSectionRect = sectionRects.length > 0 ? sectionRects[sectionRects.length - 1] : null;
    // boundary after the last section
    boundaries.push({
      sectionIndex: this.sectionGrids.length,
      orientation: 'horizontal',
      x: 0,
      y: lastSectionRect ? lastSectionRect.y + lastSectionRect.height + 2 * SECTION_DIVIDER_MARGIN : 0,
      width: gridWidth,
      height: BOUNDARY_THICKNESS,
      insertionRow: 0,
      insertionCol: 0,
      isFirstInRow: false,
      isLastInRow: false,
      isFirstInSection: true,
      isLastInSection: false,
      dragPolygon: null,
      forNewSection: true,
    });

    // console.log('lm _updateBoundaries', boundaries);
    this.boundaries = boundaries;
  }
}
